import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Segment, { SegmentConfig } from '@sparefoot/segment-react';
import { on, off, getWindowHeight, getBoundingClientRect } from '@sparefoot/react-core/utils/dom';
import { getSegmentContext } from '@sparefoot/react-core/segment';
import { withShowHide } from '@sparefoot/react-core/decorators';
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days';
import format from 'date-fns/format';
import {
	compose, defaultProps, withState, withHandlers, lifecycle, withPropsOnChange
} from 'recompose';
import CSSTransition from 'react-transition-group/CSSTransition';
import { Calendar } from 'components/core/Calendar';
import { Input } from 'components/core/Input';

import './Datepicker.scss';

export const enhance = compose(
	defaultProps({ today: new Date() }),
	getSegmentContext,
	withShowHide('calendar'),
	withState('instance', 'setInstance', null),
	withPropsOnChange(['calendarShowing', 'instance'], ({ instance }) => {
		if (!instance) { return null; }
		const rect = getBoundingClientRect(instance);
		const above = rect.bottom + 250 > getWindowHeight();
		return {
			position: above ? 'above' : 'below'
		};
	}),
	withHandlers({
		getRef: (props) => props.setInstance,
		handleDateClick: (props) => (date) => {
			// Track The Event
			if (date.type.invalid) {
				const dateDiff = differenceInCalendarDays(date.date, props.today);
				Segment.trackClick(SegmentConfig.buildTrackingProps(
					props.segmentConfig,
					{
						segmentLabel: `disabled calendar date-${dateDiff}`,
						segmentProperties: {
							moveInDateSelected: format(date.date, 'MM/DD/YYYY')
						}
					}
				));
			} else {
				Segment.track('valid', SegmentConfig.buildTrackingProps(
					props.segmentConfig,
					{
						segmentLabel: 'move_in_date',
						segmentProperties: {
							moveInDateSelected: format(date.date, 'MM/DD/YYYY')
						}
					}
				));
				props.onHideCalendar();

				props.onChange({
					target: {
						name: props.name,
						value: date
					}
				});
			}

			if (props.onDateClick) {
				props.onDateClick(date);
			}
		},
		handlePageClick: ({ instance, onHideCalendar }) => (event) => {
			if (!instance || !instance.contains(event.target)) {
				onHideCalendar();
			}
		}
	}),
	lifecycle({
		componentDidMount() {
			on('click', this.props.handlePageClick);
		},

		componentWillUnmount() {
			off('click', this.props.handlePageClick);
		}
	}),
);

export function Datepicker({
	allValid, calendarShowing, closures, error, getRef, handleDateClick,
	helpText, id, inputSegmentLabel, label, name, onShowCalendar, position,
	placeholder, reservationWindow, segmentLabel, today, withIcon, value
}) {
	return (
		<div ref={getRef} className={classnames('ss-datepicker', position, { 'with-icon': withIcon })}>
			<Input
				name={name}
				id={id}
				helpText={helpText}
				label={label}
				value={value}
				onFocus={onShowCalendar}
				placeholder={placeholder}
				segmentLabel={inputSegmentLabel}
				error={error}
				readOnly
			/>
			<CSSTransition
				classNames="transition-fade-in"
				in={calendarShowing}
				unmountOnExit
				timeout={150}
			>
				<Calendar
					closures={closures}
					reservationWindow={reservationWindow}
					today={today}
					selectedDate={value}
					onDateClick={handleDateClick}
					allValid={allValid}
					segmentLabel={segmentLabel}
					segmentCategory="calendar"
				/>
			</CSSTransition>
		</div>
	);
}

Datepicker.propTypes = {
	allValid: PropTypes.bool,
	calendarShowing: PropTypes.bool,
	closures: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
	error: PropTypes.string,
	getRef: PropTypes.func.isRequired,
	helpText: PropTypes.string,
	id: PropTypes.string,
	label: PropTypes.string,
	name: PropTypes.string.isRequired,
	handleDateClick: PropTypes.func.isRequired,
	onShowCalendar: PropTypes.func,
	placeholder: PropTypes.string,
	position: PropTypes.string,
	inputSegmentLabel: PropTypes.string,
	reservationWindow: PropTypes.instanceOf(Date),
	segmentLabel: PropTypes.string.isRequired,
	today: PropTypes.instanceOf(Date),
	value: PropTypes.string.isRequired,
	withIcon: PropTypes.bool
};

Datepicker.defaultProps = {
	inputSegmentLabel: 'datepicker'
};

export default enhance(Datepicker);
